// ---------------------------------------------------------------------------
// The round seen whole: two histograms, over every seat in the room.
//
// The table beside this shows eight answers. This is the other four hundred and
// ninety-two seats, and it is the reason the eight can be a top-eight without
// being a silent cap — Isaac asked for both charts and they answer different
// questions:
//
//   **What the room found**, bucketed by answer LENGTH for letters and the
//   conundrum, and by DISTANCE FROM THE TARGET for numbers. That is the same
//   split `revealOrder` makes in the engine — a numbers round is not a length
//   sort — and the buckets arrive already made, in `aggregate.histogram`.
//
//   **What the room scored.** `aggregate.scoreHistogram`, which is where the
//   shape of a round actually lives: forty people on 0 and six on 18 is a hard
//   board, and no list of eight answers says that.
//
// **`Histogram` is reused rather than reimplemented**, so the chart is a real
// `<table>` with `aria-hidden` bars in its cells and degrades to its own numbers
// rather than to a sentence about them. Everything about the bars — the minimum
// stub for a nonzero count, the opaque track, the four legal fills — is that
// component's, and none of it is re-decided here.
//
// **A bucket with a zero count is drawn.** They arrive in the payload on purpose
// (`RoundAggregate.histogram`) and a histogram with the holes punched out of it
// is not a histogram: the gap between the sixes and the eights is the fact.
// Nothing here filters them, and nothing should learn to.
// ---------------------------------------------------------------------------

import type { RoundAggregate } from '@countdown/shared';
import { Histogram } from '../../../components/charts/Histogram.js';
import type { Bar } from '../../../components/charts/Histogram.js';
import { Frame } from '../../../components/round/atoms.js';
import { aggregateOf } from './aggregate.js';
import { STAGE_FILL } from './atoms.js';
import type { RevealView } from './view.js';

/** What the left-hand chart is bucketing, in the words the round would use. */
function answerCaption(type: RevealView['round']['type']): string {
  if (type === 'numbers') return 'How close the room got';
  return 'How long the room went';
}

function answerBars(
  type: RevealView['round']['type'],
  buckets: RoundAggregate['histogram'],
): Bar[] {
  return buckets.map((bucket) => {
    if (type === 'numbers') {
      return {
        label: bucket.value === 0 ? 'exact' : String(bucket.value),
        longLabel: bucket.value === 0 ? 'exact' : `${bucket.value} away from the target`,
        value: bucket.count,
        // Exact is the one bucket worth telling apart at a glance, and it is
        // the good one, so it takes `good` and the rest take the default blue.
        tone: bucket.value === 0 ? 'good' : 'blue',
      };
    }
    return {
      label: String(bucket.value),
      longLabel: `${bucket.value} ${bucket.value === 1 ? 'letter' : 'letters'}`,
      value: bucket.count,
    };
  });
}

function scoreBars(buckets: RoundAggregate['scoreHistogram']): Bar[] {
  return buckets.map((bucket) => ({
    label: String(bucket.value),
    longLabel: `${bucket.value} ${bucket.value === 1 ? 'point' : 'points'}`,
    value: bucket.count,
    // `accent`, so the two charts are not two blue blocks a viewer has to read
    // the caption of to tell apart. It is one of the four fills the palette test
    // holds at 3:1 on the track — see `BarTone`.
    tone: 'accent' as const,
  }));
}

export function RoundDistribution({ view }: { view: RevealView }) {
  const aggregate = aggregateOf(view.round);
  if (aggregate === null) return null;

  const seats = aggregate.scoreHistogram.reduce((sum, bucket) => sum + bucket.count, 0);

  return (
    <Frame dense className={STAGE_FILL}>
      <header className="flex shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-lg font-bold sm:text-xl">The whole room</h2>
        <span className="label-mono ml-auto">
          {seats === 1 ? '1 seat' : `${seats} seats`}
        </span>
      </header>

      {/* Stacked on a phone and side by side once there is room. Two charts of
          a dozen rows each do not go one above the other on 844px, and the
          stage scrolls itself rather than the page when they cannot — which is
          what `STAGE_FILL` is for and is the only scrolling this screen has. */}
      <div className="grid min-h-0 gap-4 sm:grid-cols-2 sm:gap-6">
        <Histogram
          caption={answerCaption(view.round.type)}
          bars={answerBars(view.round.type, aggregate.histogram)}
          emptyNote="Nobody put anything up."
        />
        <Histogram
          caption="What the room scored"
          bars={scoreBars(aggregate.scoreHistogram)}
          emptyNote="Nobody scored."
        />
      </div>
    </Frame>
  );
}
